/**
 * What an answer's figures were computed from, and whether each one checks out.
 *
 * Arrives as the ANSWER_RECEIPT custom event once the answer text is final. The
 * runtime compares every figure in the prose with the governed queries that ran,
 * so a number the model wrote that no query produced is named rather than
 * trusted. Read defensively: an event this client cannot read is dropped, and
 * the answer itself is unaffected.
 */

export type CheckStatus = "verified" | "unverified" | "no_figures";
export type MatchKind =
  | "value"
  | "total"
  | "average"
  | "minimum"
  | "maximum"
  | "share"
  | "difference"
  | "change"
  | "row_count";

export interface CheckedFigure {
  text: string;
  status: "verified" | "unverified";
  approximate: boolean;
  match?: { queryId: string; member: string; kind: MatchKind };
}

export interface ReceiptFilter {
  member: string;
  operator: string;
  values: string[];
}

export interface ReceiptPeriod {
  dimension: string;
  granularity?: string;
  dateRange?: string | string[];
}

export interface ReceiptOrder {
  member: string;
  direction: "asc" | "desc";
}

export interface ReceiptJoin {
  fromEntity: string;
  toEntity: string;
  cardinality: string;
  condition: string;
}

const ENDORSEMENTS = ["promoted", "certified", "approved_with_exceptions"] as const;
export type Endorsement = (typeof ENDORSEMENTS)[number];

function endorsementOf(value: unknown): Endorsement | null {
  return ENDORSEMENTS.find((item) => item === value) ?? null;
}

export interface QueryReceipt {
  queryId: string;
  contextId: string;
  contextVersion: string;
  endorsement: Endorsement | null;
  executedAt?: string;
  measures: string[];
  units: Record<string, string>;
  dimensions: string[];
  filters: ReceiptFilter[];
  timeDimensions: ReceiptPeriod[];
  segments: string[];
  // How the rows were ranked and capped: what proves a "top 10" ran as one.
  order: ReceiptOrder[];
  rowLimit?: number;
  joins: ReceiptJoin[];
  rowCount: number;
  truncated: boolean;
  // Periods in the result that have not finished, such as the current month.
  periodNotes: string[];
  sourceSql: string[];
}

export interface AnswerReceipt {
  summary: string;
  status: CheckStatus;
  checked: number;
  verified: number;
  flagged: number;
  figures: CheckedFigure[];
  receipts: QueryReceipt[];
}

// The published contract's bounds, applied again so a misbehaving producer
// cannot make the receipt unreadable.
const MAX_FIGURES = 50;
const MAX_RECEIPTS = 12;
const MAX_ITEMS = 40;
const MAX_TEXT = 300;
const MAX_SQL = 4;
const MAX_SQL_LENGTH = 4100;
const MAX_PERIOD_NOTES = 4;
const STATUSES: readonly CheckStatus[] = ["verified", "unverified", "no_figures"];
const KINDS: readonly MatchKind[] = [
  "value", "total", "average", "minimum", "maximum", "share", "difference",
  "change", "row_count",
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function text(value: unknown, limit = MAX_TEXT): string {
  return typeof value === "string" ? value.trim().slice(0, limit) : "";
}

function count(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : 0;
}

function strings(value: unknown, limit = MAX_ITEMS, length = MAX_TEXT): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => text(item, length))
    .filter(Boolean)
    .slice(0, limit);
}

function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord).slice(0, MAX_ITEMS) : [];
}

function figure(value: Record<string, unknown>): CheckedFigure | null {
  const label = text(value.text, 80);
  const status = value.status;
  if (!label || (status !== "verified" && status !== "unverified")) return null;
  const match = isRecord(value.match) ? value.match : null;
  const kind = match?.kind;
  return {
    text: label,
    status,
    approximate: value.approximate === true,
    ...(match && typeof kind === "string" && KINDS.includes(kind as MatchKind)
      ? {
          match: {
            queryId: text(match.queryId, 64),
            member: text(match.member, 200),
            kind: kind as MatchKind,
          },
        }
      : {}),
  };
}

function receipt(value: Record<string, unknown>): QueryReceipt | null {
  const contextId = text(value.contextId, 200);
  const measures = strings(value.measures);
  if (!contextId || !measures.length) return null;
  const units = isRecord(value.units) ? value.units : {};
  return {
    queryId: text(value.queryId, 64),
    contextId,
    contextVersion: text(value.contextVersion, 40),
    endorsement: endorsementOf(value.endorsement),
    ...(text(value.executedAt) ? { executedAt: text(value.executedAt) } : {}),
    measures,
    units: Object.fromEntries(
      measures
        .filter((member) => typeof units[member] === "string")
        .map((member) => [member, text(units[member], 40)]),
    ),
    dimensions: strings(value.dimensions),
    filters: records(value.filters)
      .map((item) => ({
        member: text(item.member, 200),
        operator: text(item.operator, 40),
        values: strings(item.values, 24, 200),
      }))
      .filter((item) => item.member && item.operator),
    timeDimensions: records(value.timeDimensions)
      .map((item) => {
        const range = Array.isArray(item.dateRange)
          ? strings(item.dateRange, 2, 40)
          : text(item.dateRange, 80);
        return {
          dimension: text(item.dimension, 200),
          ...(text(item.granularity, 20) ? { granularity: text(item.granularity, 20) } : {}),
          ...(range.length ? { dateRange: range } : {}),
        };
      })
      .filter((item) => item.dimension),
    segments: strings(value.segments),
    order: records(value.order)
      .map((item) => ({ member: text(item.member, 200), direction: item.direction }))
      .filter((item): item is ReceiptOrder =>
        Boolean(item.member) && (item.direction === "asc" || item.direction === "desc")),
    ...(count(value.rowLimit) > 0 ? { rowLimit: count(value.rowLimit) } : {}),
    joins: records(value.joins)
      .map((item) => ({
        fromEntity: text(item.fromEntity, 200),
        toEntity: text(item.toEntity, 200),
        cardinality: text(item.cardinality, 40),
        condition: text(item.condition, 500),
      }))
      .filter((item) => item.fromEntity && item.toEntity),
    rowCount: count(value.rowCount),
    truncated: value.truncated === true,
    periodNotes: strings(value.periodNotes, MAX_PERIOD_NOTES, 400),
    sourceSql: strings(value.sourceSql, MAX_SQL, MAX_SQL_LENGTH),
  };
}

/** The event's value as an answer receipt, or null when it cannot be read. */
export function readAnswerReceipt(value: unknown): AnswerReceipt | null {
  if (!isRecord(value) || !isRecord(value.check)) return null;
  const check = value.check;
  const status = check.status;
  if (typeof status !== "string" || !STATUSES.includes(status as CheckStatus)) {
    return null;
  }
  const summary = text(check.summary, 1000) || text(value.summary, 1000);
  if (!summary) return null;
  return {
    summary,
    status: status as CheckStatus,
    checked: count(check.checked),
    verified: count(check.verified),
    flagged: count(check.flagged),
    figures: (Array.isArray(check.figures) ? check.figures : [])
      .filter(isRecord)
      .slice(0, MAX_FIGURES)
      .map(figure)
      .filter((item): item is CheckedFigure => item !== null),
    receipts: (Array.isArray(value.receipts) ? value.receipts : [])
      .filter(isRecord)
      .slice(0, MAX_RECEIPTS)
      .map(receipt)
      .filter((item): item is QueryReceipt => item !== null),
  };
}
